import { useMemo, useState } from 'react';
import { Switch, Button, Tabs } from 'antd';
import SheetSelect from '../components/SheetSelect';
import AppBar from '../components/AppBar';
import InfoNotes from '../components/InfoNotes';
import { RM } from '../components/kit';
import { computeDsr, estimateNetIncome, num, MAX_DSR_PCT, ELIG_RATE_PCT, ELIG_YEARS, MARITAL_OPTIONS } from '../lib/loanCalc';
import { InfoDot, NumInput, MoneyInput } from './MortgagePage';

// DSR calculator — Basic (net income) + Advanced (gross → estimated net via
// EPF/SOCSO/EIS/PCB). DSR = commitments / income; banks lend up to ~70% DSR.
// Pure client-side, nothing saved.

export default function DsrPage() {
  const [tab, setTab] = useState('basic');

  const info = () =>
    setInfoOpen(true);
  const [infoOpen, setInfoOpen] = useState(false);
  const INFO_PAGES = [
    { body: 'Calculate debt service ratio (DSR) and loan eligibility.\n\nDSR = ( Commitment / Income ) %\n\nDSR shows how much of a person\u2019s income is used to service debt instalments.' },
    { title: 'Notes', body: 'The calculator will not include the new monthly instalment in the calculation when Test Loan Eligibility is checked.\n\nDifferent banks recognise income and commitments differently, so the final DSR can vary between banks. Malaysian banks commonly lend up to ~70% DSR.' },
    { title: 'Advanced', body: 'The ADVANCED version auto-calculates net income and the eligible loan amount \u2014 estimating EPF, SOCSO/EIS and PCB (income tax) deductions from your gross income before applying the DSR limit.' },
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title="DSR" right={<InfoDot onClick={info} />} />
      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px', display: 'flex', flexDirection: 'column' }}>
        <Tabs
          activeKey={tab}
          onChange={setTab}
          centered
          items={[
            { key: 'basic', label: 'BASIC', children: <BasicTab /> },
            { key: 'advanced', label: 'ADVANCED', children: <AdvancedTab /> },
          ]}
        />
      </div>
      <InfoNotes open={infoOpen} onClose={() => setInfoOpen(false)} title="DSR" pages={INFO_PAGES} />
    </div>
  );
}

// ─── BASIC ────────────────────────────────────────────────────────────
function BasicTab() {
  const [testElig, setTestElig] = useState(false);
  const [income, setIncome] = useState('');
  const [commitments, setCommitments] = useState('');
  const [instalment, setInstalment] = useState('');
  const [show, setShow] = useState(false);

  const r = useMemo(
    () => computeDsr({ income, commitments, newInstalment: instalment, testEligibility: testElig }),
    [income, commitments, instalment, testElig]
  );

  const reset = () => { setTestElig(false); setIncome(''); setCommitments(''); setInstalment(''); setShow(false); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <ToggleRow label="Test Loan Eligibility" checked={testElig} onChange={setTestElig} />

      <Field label="Net Monthly Income (RM)">
        <MoneyInput value={income} onChange={setIncome} placeholder="" />
      </Field>
      <Field label="Total Monthly Commitments (RM)">
        <MoneyInput value={commitments} onChange={setCommitments} placeholder="" />
      </Field>
      <Field label="New Monthly Instalment Amount (RM)">
        <MoneyInput value={instalment} onChange={setInstalment} placeholder="0" />
      </Field>

      <div style={{ display: 'flex', gap: 12 }}>
        <Button block size="large" onClick={reset}>Reset</Button>
        <Button block size="large" type="primary" onClick={() => setShow(true)}>Calculate</Button>
      </div>

      {show && num(income) > 0 && <DsrResult r={r} testElig={testElig} />}
    </div>
  );
}

// ─── ADVANCED ─────────────────────────────────────────────────────────
function AdvancedTab() {
  const [testElig, setTestElig] = useState(false);
  const [marital, setMarital] = useState('SINGLE');
  const [gross, setGross] = useState('');
  const [epfOn, setEpfOn] = useState(false);
  const [commitments, setCommitments] = useState('');
  const [instalment, setInstalment] = useState('');
  const [show, setShow] = useState(false);

  const net = useMemo(() => estimateNetIncome({ grossMonthly: gross, epfOn, marital }), [gross, epfOn, marital]);
  const r = useMemo(
    () => computeDsr({ income: net.net, commitments, newInstalment: instalment, testEligibility: testElig }),
    [net.net, commitments, instalment, testElig]
  );

  const reset = () => { setTestElig(false); setMarital('SINGLE'); setGross(''); setEpfOn(false); setCommitments(''); setInstalment(''); setShow(false); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <ToggleRow label="Test Loan Eligibility" checked={testElig} onChange={setTestElig} />

      <SheetSelect title="Marital status" value={marital} onChange={setMarital} options={MARITAL_OPTIONS} />

      <Field label="Gross Monthly Income (Include Allowances)">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <MoneyInput value={gross} onChange={setGross} placeholder="0" />
          </div>
          <span style={{ fontSize: 13 }}>EPF</span>
          <Switch checked={epfOn} onChange={setEpfOn} />
        </div>
      </Field>
      <Field label="Total Monthly Commitments (RM)">
        <MoneyInput value={commitments} onChange={setCommitments} placeholder="" />
      </Field>
      <Field label="New Monthly Instalment Amount (RM)">
        <MoneyInput value={instalment} onChange={setInstalment} placeholder="0" />
      </Field>

      <div style={{ display: 'flex', gap: 12 }}>
        <Button block size="large" onClick={reset}>Reset</Button>
        <Button block size="large" type="primary" onClick={() => setShow(true)}>Calculate</Button>
      </div>

      {show && num(gross) > 0 && (
        <>
          <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '13px 15px', fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)', background: 'var(--surface2)' }}>ESTIMATED NET INCOME</div>
            <Row label="Gross monthly income" value={RM(net.gross)} />
            {epfOn && <Row label="EPF employee (11%)" value={`− ${RM(net.epf)}`} />}
            <Row label="SOCSO + EIS" value={`− ${RM(net.socso + net.eis)}`} />
            <Row label="Est. monthly tax (PCB)" value={`− ${RM(net.tax)}`} />
            <Row label="Net monthly income" value={RM(net.net)} strong />
          </div>
          <DsrResult r={r} testElig={testElig} />
        </>
      )}
    </div>
  );
}

// ─── shared result block ──────────────────────────────────────────────
function DsrResult({ r, testElig }) {
  const over = r.dsrPct > MAX_DSR_PCT;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ background: 'var(--chip)', borderRadius: 12, padding: '15px 16px', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12.5, color: 'var(--primary)' }}>Debt Service Ratio</span>
          <span style={{ fontSize: 22, fontWeight: 700, color: over ? 'var(--bad)' : 'var(--primary)' }}>{r.dsrPct.toFixed(1)}%</span>
        </div>
        <div style={{ height: 5, borderRadius: 999, background: 'color-mix(in srgb,var(--primary) 18%,transparent)', overflow: 'hidden' }}>
          <div style={{ width: `${Math.min(100, (r.dsrPct / MAX_DSR_PCT) * 100)}%`, height: '100%', background: over ? 'var(--bad)' : 'var(--primary)', transition: 'width .18s ease' }} />
        </div>
        <div style={{ fontSize: 11.5, color: over ? 'var(--bad)' : 'var(--primary)', opacity: 0.9 }}>
          {over
            ? `Above the ~${MAX_DSR_PCT}% ceiling most banks allow — commitments too high for this income.`
            : `Within the ~${MAX_DSR_PCT}% ceiling most banks allow.`}
        </div>
      </div>

      {testElig && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '13px 15px', fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)', background: 'var(--surface2)' }}>LOAN ELIGIBILITY</div>
          <Row label={`Max total commitment @ ${MAX_DSR_PCT}% DSR`} value={RM(r.maxCommitment)} />
          <Row label="Instalment headroom left" value={RM(r.headroom)} />
          <Row label={`Est. eligible loan (${ELIG_RATE_PCT}% · ${ELIG_YEARS} yrs)`} value={RM(r.eligibleLoan)} strong />
        </div>
      )}
    </div>
  );
}

// ─── shared bits ──────────────────────────────────────────────────────
const Field = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' }}>{label}</label>
    {children}
  </div>
);

const ToggleRow = ({ label, checked, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
    <span style={{ fontSize: 13 }}>{label}</span>
    <Switch checked={checked} onChange={onChange} />
  </div>
);

const Row = ({ label, value, strong }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, padding: '13px 15px', borderTop: '1px solid var(--border)', fontSize: 12.5, background: strong ? 'var(--chip)' : undefined }}>
    <span style={{ color: strong ? 'var(--primary)' : 'var(--text)', fontWeight: strong ? 600 : 400 }}>{label}</span>
    <b style={{ color: strong ? 'var(--primary)' : 'var(--text)', whiteSpace: 'nowrap' }}>{value}</b>
  </div>
);
